import React, { useEffect, useMemo, useState, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import { MultiMarkerMap } from "../components/MultiMarkerMap";
import { api, formatApiError, fileUrl } from "../lib/api";
import { logError } from "../lib/logger";
import { CategoryStars } from "../components/StarRating";
import { downloadTalentCSV, downloadTalentPDF } from "../lib/talentReport";
import { toast } from "sonner";
import {
  Map as MapIcon, Users, UserCheck, Briefcase, Loader2, MapPinOff,
  Phone, Search, Unlock, FileText, FileSpreadsheet, ClipboardList, Star,
  ListChecks, Plus, Pencil, Trash2, X, Check, GripVertical, UserCog,
} from "lucide-react";
import SuperAdminTalents from "./SuperAdminTalents";

const PIN_COLORS = { green: "#22c55e", yellow: "#f59e0b", red: "#ef4444" };
const STATUS_LABEL = { disponivel: "Disponível", ocupado: "Ocupado", inativo: "Inativo" };
const LEGEND = [
  { c: "green", t: "Livre" }, { c: "yellow", t: "Ocupado" }, { c: "red", t: "Inativo" },
];

function buildPopupNode(p, onRelease) {
  const el = document.createElement("div");
  el.style.width = "220px";
  const photo = p.photo_path
    ? `<img src="${fileUrl(p.photo_path)}" style="width:44px;height:44px;border-radius:50%;object-fit:cover" />`
    : `<div style="width:44px;height:44px;border-radius:50%;background:#f1f5f9;display:flex;align-items:center;justify-content:center;font-weight:700;color:#94a3b8">${(p.name || "?").charAt(0)}</div>`;
  const assigned = p.status === "ocupado" && p.assigned_tenant_name
    ? `<div style="margin-top:4px;font-size:11px;color:#b45309">💼 ${p.assigned_tenant_name}${p.assignment_type === "fixo" ? " · fixo" : ""}</div>`
    : "";
  el.innerHTML = `
    <div style="display:flex;gap:8px;align-items:center">
      ${photo}
      <div style="min-width:0">
        <div style="font-weight:600;font-size:13px">${p.name || ""}</div>
        <div style="font-size:11px;color:#64748b">${p.city || "—"} · ${STATUS_LABEL[p.status] || ""}</div>
      </div>
    </div>
    <div style="font-size:11px;color:#475569;margin-top:6px;line-height:1.5">
      <div>🚗 ${p.has_car ? "Sim" : "Não"} · 🪪 ${p.has_license ? "Driver" : "Não"}${p.rating != null ? ` · ⭐ ${p.rating}` : ""}</div>
      ${p.travel_radius_miles != null ? `<div style="margin-top:2px">📍 Alcance: até ${p.travel_radius_miles} mi</div>` : ""}
      ${assigned}
    </div>`;
  const actions = document.createElement("div");
  actions.style.cssText = "display:flex;gap:6px;margin-top:8px";
  if (p.phone) {
    const call = document.createElement("a");
    call.href = `tel:${p.phone}`;
    call.textContent = "Contato";
    call.style.cssText = "flex:1;text-align:center;font-size:11px;font-weight:600;padding:6px;border-radius:8px;background:#ecfdf5;color:#047857;text-decoration:none";
    actions.appendChild(call);
  }
  if (p.status === "ocupado") {
    const btn = document.createElement("button");
    btn.textContent = "Liberar";
    btn.setAttribute("data-testid", `satm-release-${p.id}`);
    btn.style.cssText = "flex:1;font-size:11px;font-weight:600;padding:6px;border-radius:8px;background:#fef2f2;color:#b91c1c;border:none;cursor:pointer";
    btn.onclick = () => onRelease(p.id, p.name);
    actions.appendChild(btn);
  }
  el.appendChild(actions);
  return el;
}

export default function SuperAdminTalentMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tab, setTab] = useState("mapa"); // mapa | registro | modalidades
  const [categories, setCategories] = useState([]);
  const [rateTarget, setRateTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/super-admin/talent-bank/map");
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const r = await api.get("/rating-categories");
      setCategories(Array.isArray(r.data) ? r.data : []);
    } catch (e) { logError("TalentMap: falha ao carregar categorias", e); }
  }, []);

  useEffect(() => { load(); loadCategories(); }, [load, loadCategories]);

  const onRelease = useCallback(async (id, name) => {
    if (!window.confirm(`Liberar ${name || "este colaborador"} de volta ao pool?`)) return;
    try {
      await api.post(`/super-admin/talent-bank/${id}/release`);
      toast.success("Colaborador liberado — disponível novamente");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }, [load]);

  const points = useMemo(() => data?.points || [], [data]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return points.filter((p) => {
      if (statusFilter && p.status !== statusFilter) return false;
      if (ql) {
        const hay = `${p.name || ""} ${p.city || ""} ${p.assigned_tenant_name || ""}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [points, q, statusFilter]);

  const markers = useMemo(
    () => filtered
      .filter((p) => p.lat != null && p.lng != null)
      .map((p) => ({
        lat: p.lat, lng: p.lng,
        color: PIN_COLORS[p.color] || PIN_COLORS.green,
        popupNode: buildPopupNode(p, onRelease),
      })),
    [filtered, onRelease],
  );

  const TABS = [
    { id: "mapa", label: "Mapa", icon: MapIcon },
    { id: "registro", label: "Registro", icon: ClipboardList },
    { id: "modalidades", label: "Modalidades", icon: ListChecks },
    { id: "gerenciar", label: "Gerenciar", icon: UserCog },
  ];

  return (
    <div className="space-y-4 text-slate-100" data-testid="super-admin-talent-map">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <MapIcon className="text-fuchsia-400" /> Mapa de Helps (global)
          </h1>
          <p className="text-sm text-violet-300/70">Todos os colaboradores do banco, em todos os tenants</p>
        </div>
        {tab === "mapa" && (
          <div className="flex items-center gap-3 text-xs">
            {LEGEND.map((l) => (
              <span key={l.c} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ background: PIN_COLORS[l.c] }} /> {l.t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Sub-abas */}
      <div className="inline-flex rounded-xl bg-slate-900/60 border border-violet-800/40 p-1" data-testid="satm-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`satm-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${active ? "bg-fuchsia-600 text-white" : "text-violet-200 hover:bg-slate-800/60"}`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "mapa" && (
        <MapTab
          data={data} loading={loading} markers={markers} filtered={filtered}
          q={q} setQ={setQ} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          onRelease={onRelease}
        />
      )}

      {tab === "registro" && (
        <RegistroTab points={points} onRate={(p) => setRateTarget(p)} />
      )}

      {tab === "modalidades" && (
        <ModalidadesTab categories={categories} reload={loadCategories} />
      )}

      {tab === "gerenciar" && (
        <div data-testid="satm-tab-gerenciar-content">
          <SuperAdminTalents />
        </div>
      )}

      {rateTarget && (
        <RateModal
          talent={rateTarget} categories={categories}
          onClose={() => setRateTarget(null)}
          onSaved={() => { setRateTarget(null); load(); }}
        />
      )}
    </div>
  );
}

/* ---------------- Aba Mapa ---------------- */
function MapTab({ data, loading, markers, filtered, q, setQ, statusFilter, setStatusFilter, onRelease }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="satm-stats">
        <Stat icon={Users} label="Total" value={data?.total} color="text-violet-300" />
        <Stat icon={UserCheck} label="Livres" value={data?.livres} color="text-emerald-400" />
        <Stat icon={Briefcase} label="Ocupados" value={data?.ocupados} color="text-amber-400" />
        <Stat icon={MapIcon} label="No mapa" value={data?.mapped} color="text-sky-400" />
        <Stat icon={MapPinOff} label="Sem geo" value={data?.no_geo} color="text-rose-400" />
      </div>

      <div className="bg-slate-900/60 border border-violet-800/40 rounded-xl p-3 flex flex-wrap gap-2 items-center mt-4" data-testid="satm-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="satm-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100 placeholder:text-slate-500"
            placeholder="Buscar por nome, cidade ou empresa…"
            value={q} onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="satm-status"
          className="w-[150px] px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100"
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="disponivel">Livres</option>
          <option value="ocupado">Ocupados</option>
          <option value="inativo">Inativos</option>
        </select>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-4 mt-4">
        <div className="bg-slate-900 rounded-xl border border-violet-800/40 overflow-hidden" style={{ height: 560 }} data-testid="satm-map">
          {loading && markers.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-500"><Loader2 className="animate-spin" /></div>
          ) : (
            <MultiMarkerMap markers={markers} height={560} zoom={4} />
          )}
        </div>

        <div className="bg-slate-900/60 border border-violet-800/40 rounded-xl p-3 overflow-y-auto" style={{ height: 560 }} data-testid="satm-list">
          <div className="text-sm font-semibold mb-2 text-violet-200">{filtered.length} colaborador(es)</div>
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center" data-testid="satm-empty">Nenhum colaborador.</div>
          ) : filtered.map((p) => (
            <div key={p.id} className="flex items-center gap-2.5 py-2.5 border-b border-slate-800 last:border-0" data-testid={`satm-row-${p.id}`}>
              <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ background: PIN_COLORS[p.color] || PIN_COLORS.green }} />
              {p.photo_path ? (
                <img src={fileUrl(p.photo_path)} alt={p.name} className="w-9 h-9 rounded-full object-cover flex-none" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-500 text-xs font-bold flex-none">
                  {(p.name || "?").charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-slate-100">{p.name || "—"}</div>
                <div className="text-xs text-slate-400 truncate">
                  {p.city || "—"}
                  {p.rating != null && <span className="text-amber-400"> · ⭐ {p.rating}</span>}
                  {p.travel_radius_miles != null && <span className="text-sky-400"> · 📍 {p.travel_radius_miles} mi</span>}
                  {p.status === "ocupado" && p.assigned_tenant_name && (
                    <span className="text-amber-400"> · 💼 {p.assigned_tenant_name}</span>
                  )}
                  {p.lat == null && <span className="text-rose-400"> · sem geo</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-none">
                {p.phone && (
                  <a href={`tel:${p.phone}`} title="Contato" data-testid={`satm-call-${p.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-400 hover:bg-emerald-500/10">
                    <Phone size={15} />
                  </a>
                )}
                {p.status === "ocupado" && (
                  <button onClick={() => onRelease(p.id, p.name)} title="Liberar" data-testid={`satm-list-release-${p.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-rose-400 hover:bg-rose-500/10">
                    <Unlock size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ---------------- Aba Registro (relatório PDF/CSV) ---------------- */
function RegistroTab({ points, onRate }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [ratedFilter, setRatedFilter] = useState(""); // "" | rated | unrated

  const cities = useMemo(() => {
    const set = new Set(points.map((p) => (p.city || "").trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [points]);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return points
      .filter((p) => {
        if (statusFilter && p.status !== statusFilter) return false;
        if (cityFilter && (p.city || "").trim() !== cityFilter) return false;
        if (ratedFilter === "rated" && p.rating == null) return false;
        if (ratedFilter === "unrated" && p.rating != null) return false;
        if (ql && !`${p.name || ""} ${p.city || ""} ${p.phone || ""}`.toLowerCase().includes(ql)) return false;
        return true;
      })
      .map((p) => ({ id: p.id, name: p.name, phone: p.phone, city: p.city, rating: p.rating, ratings: p.ratings, travel_radius_miles: p.travel_radius_miles }));
  }, [points, q, statusFilter, cityFilter, ratedFilter]);

  const filterLabel = () => {
    const parts = [];
    if (statusFilter) parts.push(STATUS_LABEL[statusFilter] || statusFilter);
    if (cityFilter) parts.push(cityFilter);
    if (ratedFilter === "rated") parts.push("Avaliados");
    if (ratedFilter === "unrated") parts.push("Sem avaliação");
    return parts.length ? ` · ${parts.join(" · ")}` : "";
  };

  return (
    <div className="bg-slate-900/60 border border-violet-800/40 rounded-xl p-4" data-testid="satm-registro">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><ClipboardList size={18} className="text-fuchsia-400" /> Registro de colaboradores</h2>
          <p className="text-xs text-violet-300/70">Relatório com nome, telefone e cidade — {rows.length} registro(s){filterLabel()}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadTalentPDF(rows)} disabled={rows.length === 0} data-testid="satm-report-pdf"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50">
            <FileText size={15} /> Baixar PDF
          </button>
          <button onClick={() => downloadTalentCSV(rows)} disabled={rows.length === 0} data-testid="satm-report-csv"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
            <FileSpreadsheet size={15} /> Baixar Excel/CSV
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="satm-registro-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100 placeholder:text-slate-500"
            placeholder="Buscar por nome, cidade ou telefone…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="satm-registro-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="w-[150px] px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100">
          <option value="">Todos os status</option>
          <option value="disponivel">Livres</option>
          <option value="ocupado">Ocupados</option>
          <option value="inativo">Inativos</option>
        </select>
        <select data-testid="satm-registro-city" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
          className="w-[180px] px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100">
          <option value="">Todas as cidades</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="satm-registro-rated" value={ratedFilter} onChange={(e) => setRatedFilter(e.target.value)}
          className="w-[170px] px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100">
          <option value="">Todas as avaliações</option>
          <option value="rated">Avaliados</option>
          <option value="unrated">Sem avaliação</option>
        </select>
        {(statusFilter || cityFilter || ratedFilter || q) && (
          <button onClick={() => { setStatusFilter(""); setCityFilter(""); setRatedFilter(""); setQ(""); }} data-testid="satm-registro-clear"
            className="px-3 py-2 rounded-lg border border-slate-700 text-slate-300 text-sm hover:bg-slate-800/60">Limpar</button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full text-sm" data-testid="satm-registro-table">
          <thead>
            <tr className="bg-slate-950/80 text-violet-200 text-left">
              <th className="px-3 py-2 font-semibold">Nome</th>
              <th className="px-3 py-2 font-semibold">Telefone</th>
              <th className="px-3 py-2 font-semibold">Cidade</th>
              <th className="px-3 py-2 font-semibold">Alcance</th>
              <th className="px-3 py-2 font-semibold">Avaliação</th>
              <th className="px-3 py-2 font-semibold text-right">Ação</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500" data-testid="satm-registro-empty">Nenhum colaborador registrado.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`satm-registro-row-${r.id}`}>
                <td className="px-3 py-2 text-slate-100">{r.name || "—"}</td>
                <td className="px-3 py-2 text-slate-300">{r.phone || "—"}</td>
                <td className="px-3 py-2 text-slate-300">{r.city || "—"}</td>
                <td className="px-3 py-2 text-slate-300">{r.travel_radius_miles != null ? `${r.travel_radius_miles} mi` : "—"}</td>
                <td className="px-3 py-2">{r.rating != null ? <span className="text-amber-400 font-semibold">⭐ {r.rating}</span> : <span className="text-slate-500">—</span>}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => onRate(r)} data-testid={`satm-rate-${r.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-xs font-semibold">
                    <Star size={13} /> Avaliar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Aba Modalidades (CRUD + reordenar) ---------------- */
function ModalidadesTab({ categories, reload }) {
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [order, setOrder] = useState(categories);
  const [dragId, setDragId] = useState(null);

  useEffect(() => { setOrder(categories); }, [categories]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/rating-categories", { name });
      setNewName("");
      toast.success("Modalidade adicionada");
      reload();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveEdit = async (id) => {
    const name = editName.trim();
    if (!name) return;
    try {
      await api.patch(`/rating-categories/${id}`, { name });
      setEditId(null);
      toast.success("Modalidade atualizada");
      reload();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Excluir a modalidade "${name}"?`)) return;
    try {
      await api.delete(`/rating-categories/${id}`);
      toast.success("Modalidade excluída");
      reload();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const persistOrder = async (ids) => {
    try {
      await api.post("/rating-categories/reorder", { ids });
      toast.success("Ordem atualizada");
      reload();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); reload(); }
  };

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = order.map((c) => c.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    const next = [...order];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrder(next);
    setDragId(null);
    persistOrder(next.map((c) => c.id));
  };

  return (
    <div className="bg-slate-900/60 border border-violet-800/40 rounded-xl p-4 max-w-2xl" data-testid="satm-modalidades">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-1"><ListChecks size={18} className="text-fuchsia-400" /> Modalidades de avaliação</h2>
      <p className="text-xs text-violet-300/70 mb-4">Estas modalidades valem para todas as empresas. As notas (1–5 estrelas) são dadas por elas ao avaliar cada colaborador. Arraste pelo ícone <GripVertical size={12} className="inline" /> para reordenar.</p>

      <div className="flex items-center gap-2 mb-4">
        <input data-testid="satm-cat-new-name" value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Nova modalidade (ex.: Comunicação)"
          className="flex-1 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100 placeholder:text-slate-500" />
        <button onClick={add} disabled={saving || !newName.trim()} data-testid="satm-cat-add"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-fuchsia-600 text-white text-sm font-semibold hover:bg-fuchsia-700 disabled:opacity-50">
          {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Adicionar
        </button>
      </div>

      <div className="space-y-2" data-testid="satm-cat-list">
        {order.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">Nenhuma modalidade cadastrada.</div>
        ) : order.map((c) => (
          <div key={c.id}
            draggable={editId !== c.id}
            onDragStart={() => setDragId(c.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(c.id)}
            className={`flex items-center gap-2 bg-slate-950/60 border rounded-lg px-3 py-2 ${dragId === c.id ? "border-fuchsia-500 opacity-60" : "border-slate-800"} ${editId !== c.id ? "cursor-grab active:cursor-grabbing" : ""}`}
            data-testid={`satm-cat-${c.id}`}>
            {editId === c.id ? (
              <>
                <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveEdit(c.id)}
                  data-testid={`satm-cat-edit-input-${c.id}`}
                  className="flex-1 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-sm text-slate-100" />
                <button onClick={() => saveEdit(c.id)} data-testid={`satm-cat-save-${c.id}`} className="p-1.5 rounded text-emerald-400 hover:bg-emerald-500/10"><Check size={16} /></button>
                <button onClick={() => setEditId(null)} className="p-1.5 rounded text-slate-400 hover:bg-slate-700/40"><X size={16} /></button>
              </>
            ) : (
              <>
                <GripVertical size={16} className="text-slate-500 flex-none" data-testid={`satm-cat-drag-${c.id}`} />
                <Star size={15} className="text-amber-400 flex-none" />
                <span className="flex-1 text-sm text-slate-100">{c.name}</span>
                <button onClick={() => { setEditId(c.id); setEditName(c.name); }} data-testid={`satm-cat-edit-${c.id}`} className="p-1.5 rounded text-sky-400 hover:bg-sky-500/10"><Pencil size={15} /></button>
                <button onClick={() => remove(c.id, c.name)} data-testid={`satm-cat-delete-${c.id}`} className="p-1.5 rounded text-rose-400 hover:bg-rose-500/10"><Trash2 size={15} /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Modal de avaliação (super admin) ---------------- */
function RateModal({ talent, categories, onClose, onSaved }) {
  const [ratings, setRatings] = useState(talent.ratings || {});
  const [saving, setSaving] = useState(false);
  const setRating = (catId, star) => setRatings((p) => ({ ...p, [catId]: star }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(ratings || {}).filter(([, v]) => v > 0));
      await api.patch(`/super-admin/talent-bank/${talent.id}/ratings`, { ratings: clean });
      toast.success("Avaliação salva");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-violet-800/50 rounded-2xl w-full max-w-md p-6 text-slate-100" onClick={(e) => e.stopPropagation()} data-testid="satm-rate-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold flex items-center gap-2"><Star size={18} className="text-amber-400" /> Avaliar {talent.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={20} /></button>
        </div>
        <p className="text-xs text-violet-300/70 mb-4">Dê de 1 a 5 estrelas em cada modalidade.</p>
        {categories.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">Nenhuma modalidade cadastrada. Crie na aba "Modalidades".</div>
        ) : (
          <div className="bg-slate-950/50 rounded-xl p-3">
            <CategoryStars categories={categories} values={ratings} onChange={setRating} testid="satm-rate-stars" />
          </div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-300 font-semibold text-sm">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="satm-rate-save"
            className="flex-1 py-2.5 rounded-xl bg-fuchsia-600 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="animate-spin" size={16} />} Salvar avaliação
          </button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="bg-slate-900/60 border border-violet-800/40 rounded-xl p-4">
    <Icon className={color} size={20} />
    <div className="text-2xl font-bold mt-2 text-white">{value ?? "—"}</div>
    <div className="text-xs text-slate-400">{label}</div>
  </div>
);
